// src/App.jsx
import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import OfflineScreen from "./components/ui/OfflineScreen.jsx";
import SessionExpiredModal from "./components/ui/SessionExpiredModal.jsx";
import AppUpdateBanner from "./components/ui/AppUpdateBanner.jsx";

import Overview from "./pages/lender-portal/Overview.jsx";
import Profile from "./pages/lender-portal/Profile.jsx";
import Settings from "./pages/lender-portal/Settings.jsx";
import ListedProducts from "./pages/lender-portal/ListedProducts/ListedProducts.jsx";
import AnalyticsPage from "./pages/lender-portal/Analytics.jsx";
import UploadLayout from "./pages/lender-portal/upload/UploadLayout.jsx";

import Signin from "./pages/lender-portal/signin.jsx";
import LenderVerifyOtp from "./pages/lender-portal/LenderVerifyOtp.jsx";
import ForgotPassword from "./pages/lender-portal/ForgotPassword.jsx";

import BorrowerSourcing from "./pages/mortgages/BorrowerSourcing.jsx";
import ComplexBtlSourcing from "./pages/mortgages/ComplexBtlSourcing.jsx";
import InvoiceFinanceSourcing from "./pages/invoice-finance/InvoiceFinanceSourcing.jsx";
import BorrowerLayout from "./pages/borrower/PortalLayout.jsx";
import BorrowerDashboard from "./pages/borrower/Dashboard.jsx";
import ShortlistedProducts from "./pages/borrower/ShortlistedProducts.jsx";
import Applications from "./pages/borrower/TrackApplications.jsx";
import BorrowerProfile from "./pages/borrower/BorrowerProfile.jsx";
import BorrowerSettings from "./pages/borrower/BorrowerSettings.jsx";
import NewApplication from "./pages/borrower/NewApplication.jsx";

import BorrowerSignIn from "./pages/borrower/BorrowerSignIn.jsx";
import BorrowerVerifyOtp from "./pages/borrower/BorrowerVerifyOtp.jsx";
import BorrowerSignUp from "./pages/borrower/BorrowerSignUp.jsx";
import BorrowerForgotPassword from "./pages/borrower/ForgotPassword.jsx";
import ResetPassword from "./pages/borrower/ResetPassword.jsx";
import VerifyAccount from "./pages/borrower/VerifyAccount.jsx";

import Home from "./pages/landing/Home.jsx";
import Borrowers from "./pages/landing/Borrowers.jsx";
import Lenders from "./pages/landing/Lenders.jsx";
import BTLMortgageCalculator from "./pages/landing/BTLMortgageCalculator.jsx";
import ExploreBorrowerJourney from "./pages/landing/ExploreBorrowerJourney.jsx";
import BlogsNews from "./pages/landing/BlogsNews.jsx";
import ProductBlogCategoryPage from "./pages/landing/ProductBlogCategoryPage.jsx";
import InvoiceFinanceCalculator from "./pages/landing/InvoiceFinanceCalculator.jsx";
import AssetFinanceCalculator from "./pages/landing/AssetFinanceCalculator.jsx";
import CommercialCalculator from "./pages/landing/CommercialCalculator.jsx";
import SemiCommercialCalculator from "./pages/landing/SemiCommercialCalculator.jsx";
import BridgingCalculator from "./pages/landing/BridgingCalculator.jsx";
import DevelopmentCalculator from "./pages/landing/DevelopmentCalculator.jsx";
import About from "./pages/landing/About.jsx";
import ContactUs from "./pages/landing/ContactUs.jsx";
import PrivacyPolicy from "./pages/landing/PrivacyPolicy.jsx";

import LenderShell from "./components/ui/LenderShell.jsx";

import { apiGet, extractMessage, clearGuestSessionCookie, hasAuthenticatedCookieSession } from "./api/client";

import { RequireAdminAuth } from "./pages/admin/adminRoutes.jsx";
import AdminLayout from "./pages/admin/AdminLayout.jsx";
import AdminDashboard from "./pages/admin/AdminDashboard.jsx";
import AnalyticsReportsPage from "./pages/admin/AnalyticsReportsPage.jsx";
import BorrowerManagementPage from "./pages/admin/BorrowerManagementPage.jsx";
import LenderManagementPage from "./pages/admin/LenderManagementPage.jsx";
import ProductManagementPage from "./pages/admin/ProductManagementPage.jsx";
import UserRoleManagementPage from "./pages/admin/UserRoleManagementPage.jsx";
import ApplicationCalendarPage from "./pages/admin/ApplicationCalendarPage.jsx";
import ReviewsEngagementPage from "./pages/admin/ReviewsEngagementPage.jsx";
import RequestedCallbacksPage from "./pages/admin/RequestedCallbacksPage.jsx";
import AdminSignIn from "./pages/admin/AdminSignIn.jsx";

/* ---------- auth helpers ---------- */
import { isAuthenticated, isAuthedForPortal } from "./utils/auth.js";

function RequireAuth({ children, to = "/signin", portal }) {
  const location = useLocation();
  const allowed = portal ? isAuthedForPortal(portal) : isAuthenticated();
  if (!allowed) {
    return (
      <Navigate
        to={to}
        replace
        state={{ next: location.pathname + location.search }}
      />
    );
  }
  return children;
}

/* ---------- APP ROOT ---------- */
// Full page load so "/" is served by the Next.js marketing site, not this app's Home route.
function RedirectToSiteHome() {
  useEffect(() => {
    window.location.replace("/");
  }, []);
  return null;
}

export default function App() {
  const location = useLocation();

  const getPageTitle = React.useCallback((pathname) => {
    const exactTitles = {
      "/": "Dynesourcer | Home",
      "/borrowers": "Dynesourcer | Borrowers",
      "/lenders": "Dynesourcer | Lenders",
      "/buy-to-let-mortgage-calculator": "Dynesourcer | Buy to Let Mortgage Calculator",
      "/btl-mortgage-calculator": "Dynesourcer | Buy to Let Mortgage Calculator",
      "/explore-borrower-journey": "Dynesourcer | Explore Borrower Journey",
      "/blog": "Dynesourcer | Blogs & News",
      "/blog/buy-to-let-mortgage": "Dynesourcer | Buy to Let Blogs & News",
      "/blog/invoice-finance": "Dynesourcer | Invoice Finance Blogs & News",
      "/blog/asset-finance": "Dynesourcer | Asset Finance Blogs & News",
      "/blog/complex-btl": "Dynesourcer | Complex BTL Blogs & News",
      "/blog/commercial": "Dynesourcer | Commercial Blogs & News",
      "/blog/semi-commercial": "Dynesourcer | Semi-commercial Blogs & News",
      "/blog/bridging": "Dynesourcer | Bridging Blogs & News",
      "/blog/development": "Dynesourcer | Development Blogs & News",
      "/invoice-finance-calculator-uk": "Dynesourcer | Invoice Finance Calculator UK",
      "/invoice-finance": "Dynesourcer | Invoice Finance Calculator UK",
      "/asset-finance-calculator-uk": "Dynesourcer | Asset Finance Calculator UK",
      "/asset-finance": "Dynesourcer | Asset Finance Calculator UK",
      "/complex-btl": "Dynesourcer | Complex BTL Calculator UK",
      "/commercial-calculator-uk": "Dynesourcer | Commercial Mortgage Calculator UK",
      "/commercial": "Dynesourcer | Commercial Mortgage Calculator UK",
      "/semi-commercial-calculator-uk": "Dynesourcer | Semi-Commercial Calculator UK",
      "/semi-commercial": "Dynesourcer | Semi-Commercial Calculator UK",
      "/bridging-calculator-uk": "Dynesourcer | Bridging Finance Calculator UK",
      "/bridging": "Dynesourcer | Bridging Finance Calculator UK",
      "/development-calculator-uk": "Dynesourcer | Development Finance Calculator UK",
      "/development": "Dynesourcer | Development Finance Calculator UK",
      "/about": "Dynesourcer | About",
      "/contact": "Dynesourcer | Contact",
      "/privacy": "Dynesourcer | Privacy Policy",
      "/terms": "Dynesourcer | Terms & Privacy",
      "/mortgages/buy-to-let-mortgage-calculator": "Dynesourcer | Buy to Let Mortgage Sourcing",
      "/invoice-finance/invoice-finance-sourcing": "Dynesourcer | Invoice Finance Sourcing",
      "/mortgages/complex-buy-to-let-mortgage-calculator": "Dynesourcer | Complex BTL Mortgage Sourcing",
      "/mortgages/complex-btl-calculator": "Dynesourcer | Complex BTL Mortgage Sourcing",
      "/sourcing/buy-to-let-mortgage-sourcing": "Dynesourcer | Buy to Let Mortgage Sourcing",
      "/sourcing/complex-btl": "Dynesourcer | Complex BTL Mortgage Sourcing",
      "/sourcing/btl": "Dynesourcer | Buy to Let Mortgage Sourcing",
      "/complex-btl-calculator-uk": "Dynesourcer | Complex BTL Mortgage Sourcing",
      "/borrower/signin": "Dynesourcer | Borrower Sign In",
      "/borrower/verify-otp": "Dynesourcer | Verify Sign In",
      "/borrower/signup": "Dynesourcer | Borrower Sign Up",
      "/borrower/forgot-password": "Dynesourcer | Borrower Forgot Password",
      "/reset-password": "Dynesourcer | Reset Password",
      "/verify": "Dynesourcer | Verify Account",
      "/signin": "Dynesourcer | Lender Sign In",
      "/signin/verify-otp": "Dynesourcer | Verify Sign In",
      "/admin/analytics": "Dynesourcer | Analytics & Reports",
      "/admin/reviews": "Dynesourcer | Reviews & Engagement",
      "/admin/callbacks": "Dynesourcer | Requested Callbacks",
      "/forgot-password": "Dynesourcer | Forgot Password",
    };

    if (exactTitles[pathname]) return exactTitles[pathname];

    if (pathname.startsWith("/borrower/dashboard")) return "Dynesourcer | Borrower Dashboard";
    if (pathname.startsWith("/borrower/sourcing")) return "Dynesourcer | Borrower Sourcing";
    if (pathname.startsWith("/borrower/shortlisted")) return "Dynesourcer | Shortlisted Products";
    if (pathname.startsWith("/borrower/applications")) return "Dynesourcer | Track Applications";
    if (pathname.startsWith("/borrower/apply")) return "Dynesourcer | New Application";
    if (pathname.startsWith("/borrower/profile")) return "Dynesourcer | Borrower Profile";
    if (pathname.startsWith("/borrower/settings")) return "Dynesourcer | Borrower Settings";

    if (pathname.startsWith("/lender/overview")) return "Dynesourcer | Lender Overview";
    if (pathname.startsWith("/lender/upload")) return "Dynesourcer | Upload Products";
    if (pathname.startsWith("/lender/listed")) return "Dynesourcer | Listed Products";
    if (pathname.startsWith("/lender/analytics")) return "Dynesourcer | Lender Analytics";
    if (pathname.startsWith("/lender/profile")) return "Dynesourcer | Lender Profile";
    if (pathname.startsWith("/lender/settings")) return "Dynesourcer | Lender Settings";

    return "Dynesourcer";
  }, []);

  // Validate session on app load: prefer cookie session when present, else API tokens.
  useEffect(() => {
    if (!isAuthenticated()) return;
    clearGuestSessionCookie();

    if (hasAuthenticatedCookieSession()) {
      apiGet("/api/method/frappe.auth.get_logged_user")
        .then((res) => {
          const user = extractMessage(res);
          if (!user) console.warn("⚠️ Session validation returned no user");
        })
        .catch((err) => {
          console.warn("⚠️ Session validation failed:", err?.message);
        });
      return;
    }

    const apiKey = sessionStorage.getItem("API_KEY") || localStorage.getItem("API_KEY");
    const apiSecret = sessionStorage.getItem("API_SECRET") || localStorage.getItem("API_SECRET");
    if (!apiKey || !apiSecret) {
      console.warn("⚠️ Authenticated locally but no cookie session or API credentials found");
    }
  }, []);

  // When user returns to tab, re-check cookie session when available.
  useEffect(() => {
    const onFocus = () => {
      if (!isAuthenticated()) return;
      const path = window.location.pathname || "";
      if (
        !path.startsWith("/borrower") &&
        !path.startsWith("/lender") &&
        !path.startsWith("/admin") &&
        !path.startsWith("/mortgages")
      ) {
        return;
      }

      clearGuestSessionCookie();
      if (!hasAuthenticatedCookieSession()) return;

      apiGet("/api/method/frappe.auth.get_logged_user")
        .then((res) => { if (!extractMessage(res)) console.warn("⚠️ Session returned no user"); })
        .catch(() => {});
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  useEffect(() => {
    const helmetManaged =
      location.pathname === "/" ||
      location.pathname === "/blog" ||
      location.pathname.startsWith("/blog/");
    if (helmetManaged) return;
    document.title = getPageTitle(location.pathname);
  }, [getPageTitle, location.pathname]);

  return (
    <>
      <SessionExpiredModal />
      <Analytics />
      <OfflineScreen />
      <AppUpdateBanner />
      <Routes>
      {/* Public site */}
      <Route path="/" element={<Home />} />
      <Route path="/borrowers" element={<Borrowers />} />
      <Route path="/lenders" element={<Lenders />} />
      <Route path="/buy-to-let-mortgage-calculator" element={<BTLMortgageCalculator />} />
      <Route path="/btl-mortgage-calculator" element={<Navigate to="/buy-to-let-mortgage-calculator" replace />} />
      <Route path="/explore-borrower-journey" element={<ExploreBorrowerJourney />} />
      <Route path="/blog" element={<BlogsNews />} />
      <Route path="/blogs-news" element={<Navigate to="/blog" replace />} />
      <Route
        path="/blog/buy-to-let-mortgage"
        element={<ProductBlogCategoryPage productKey="buy-to-let-mortgage" />}
      />
      <Route
        path="/blog/buy-to-let-mortgage/:postSlug"
        element={<ProductBlogCategoryPage productKey="buy-to-let-mortgage" />}
      />
      <Route path="/blogs-news/buy-to-let-mortgage" element={<Navigate to="/blog/buy-to-let-mortgage" replace />} />
      <Route
        path="/blog/invoice-finance"
        element={<ProductBlogCategoryPage productKey="invoice-finance" />}
      />
      <Route
        path="/blog/invoice-finance/:postSlug"
        element={<ProductBlogCategoryPage productKey="invoice-finance" />}
      />
      <Route path="/blogs-news/invoice-finance" element={<Navigate to="/blog/invoice-finance" replace />} />
      <Route
        path="/blog/asset-finance"
        element={<ProductBlogCategoryPage productKey="asset-finance" />}
      />
      <Route
        path="/blog/asset-finance/:postSlug"
        element={<ProductBlogCategoryPage productKey="asset-finance" />}
      />
      <Route path="/blogs-news/asset-finance" element={<Navigate to="/blog/asset-finance" replace />} />
      <Route
        path="/blog/complex-btl"
        element={<ProductBlogCategoryPage productKey="complex-btl" />}
      />
      <Route
        path="/blog/complex-btl/:postSlug"
        element={<ProductBlogCategoryPage productKey="complex-btl" />}
      />
      <Route path="/blogs-news/complex-btl" element={<Navigate to="/blog/complex-btl" replace />} />
      <Route
        path="/blog/commercial"
        element={<ProductBlogCategoryPage productKey="commercial" />}
      />
      <Route
        path="/blog/commercial/:postSlug"
        element={<ProductBlogCategoryPage productKey="commercial" />}
      />
      <Route path="/blogs-news/commercial" element={<Navigate to="/blog/commercial" replace />} />
      <Route
        path="/blog/semi-commercial"
        element={<ProductBlogCategoryPage productKey="semi-commercial" />}
      />
      <Route
        path="/blog/semi-commercial/:postSlug"
        element={<ProductBlogCategoryPage productKey="semi-commercial" />}
      />
      <Route path="/blogs-news/semi-commercial" element={<Navigate to="/blog/semi-commercial" replace />} />
      <Route
        path="/blog/bridging"
        element={<ProductBlogCategoryPage productKey="bridging" />}
      />
      <Route
        path="/blog/bridging/:postSlug"
        element={<ProductBlogCategoryPage productKey="bridging" />}
      />
      <Route path="/blogs-news/bridging" element={<Navigate to="/blog/bridging" replace />} />
      <Route
        path="/blog/development"
        element={<ProductBlogCategoryPage productKey="development" />}
      />
      <Route
        path="/blog/development/:postSlug"
        element={<ProductBlogCategoryPage productKey="development" />}
      />
      <Route path="/blogs-news/development" element={<Navigate to="/blog/development" replace />} />
      <Route path="/invoice-finance-calculator-uk" element={<InvoiceFinanceCalculator />} />
      <Route path="/invoice-finance" element={<Navigate to="/invoice-finance-calculator-uk" replace />} />
      <Route path="/asset-finance-calculator-uk" element={<AssetFinanceCalculator />} />
      <Route path="/asset-finance" element={<Navigate to="/asset-finance-calculator-uk" replace />} />
      <Route path="/complex-btl-calculator-uk" element={<ComplexBtlSourcing />} />
      <Route path="/complex-btl" element={<Navigate to="/mortgages/complex-buy-to-let-mortgage-calculator" replace />} />
      <Route path="/commercial-calculator-uk" element={<CommercialCalculator />} />
      <Route path="/commercial" element={<Navigate to="/commercial-calculator-uk" replace />} />
      <Route path="/semi-commercial-calculator-uk" element={<SemiCommercialCalculator />} />
      <Route path="/semi-commercial" element={<Navigate to="/semi-commercial-calculator-uk" replace />} />
      <Route path="/bridging-calculator-uk" element={<BridgingCalculator />} />
      <Route path="/bridging" element={<Navigate to="/bridging-calculator-uk" replace />} />
      <Route path="/development-calculator-uk" element={<DevelopmentCalculator />} />
      <Route path="/development" element={<Navigate to="/development-calculator-uk" replace />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<PrivacyPolicy />} />

      {/* Public sourcing */}
      <Route
        path="/mortgages/buy-to-let-mortgage-calculator"
        element={<BorrowerSourcing />}
      />
      <Route
        path="/mortgages/complex-buy-to-let-mortgage-calculator"
        element={<ComplexBtlSourcing />}
      />
      <Route path="/mortgages/complex-btl-calculator" element={<Navigate to="/mortgages/complex-buy-to-let-mortgage-calculator" replace />} />
      <Route path="/mortgages/complex-buy-to-let-calculator" element={<Navigate to="/mortgages/complex-buy-to-let-mortgage-calculator" replace />} />
      <Route path="/sourcing/buy-to-let-mortgage-sourcing" element={<Navigate to="/mortgages/buy-to-let-mortgage-calculator" replace />} />
      <Route path="/sourcing/complex-btl" element={<Navigate to="/mortgages/complex-buy-to-let-mortgage-calculator" replace />} />
      <Route path="/sourcing/btl" element={<Navigate to="/mortgages/buy-to-let-mortgage-calculator" replace />} />
      <Route path="/source" element={<Navigate to="/mortgages/buy-to-let-mortgage-calculator" replace />} />
      <Route path="/invoice-finance/invoice-finance-sourcing" element={<InvoiceFinanceSourcing />} />
      <Route path="/mortgages/invoice-finance-sourcing" element={<Navigate to="/invoice-finance/invoice-finance-sourcing" replace />} />
      <Route path="/sourcing/invoice-finance" element={<Navigate to="/invoice-finance/invoice-finance-sourcing" replace />} />
      <Route path="/sourcing/invoice" element={<Navigate to="/invoice-finance/invoice-finance-sourcing" replace />} />

      {/* Borrower auth */}
      <Route path="/borrower/signin" element={<BorrowerSignIn />} />
      <Route path="/borrower/verify-otp" element={<BorrowerVerifyOtp />} />
      <Route path="/borrower/signup" element={<BorrowerSignUp />} />
      <Route path="/borrower/forgot-password" element={<BorrowerForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify" element={<VerifyAccount />} />

      {/* Borrower portal – require borrower sign-in only */}
      <Route
        path="/borrower"
        element={
          <RequireAuth to="/borrower/signin" portal="borrower">
            <BorrowerLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<BorrowerDashboard />} />
        <Route path="sourcing" element={<BorrowerSourcing />} />
        <Route path="shortlisted" element={<ShortlistedProducts />} />
        <Route path="applications" element={<Applications />} />
        <Route path="apply" element={<NewApplication />} />
        <Route path="profile" element={<BorrowerProfile />} />
        <Route path="settings" element={<BorrowerSettings />} />

        {/* ✅ FIX: absolute redirect prevents dashboard/dashboard/dashboard loop */}
        <Route path="*" element={<Navigate to="/borrower/dashboard" replace />} />
      </Route>

      {/* Lender auth */}
      <Route path="/signin" element={<Signin />} />
      <Route path="/signin/verify-otp" element={<LenderVerifyOtp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route
        path="/lender/portal"
        element={<Navigate to="/signin?force=true&next=/lender/overview" replace />}
      />

      {/* Lender portal – require lender sign-in only */}
      <Route
        path="/lender"
        element={
          <RequireAuth to="/signin" portal="lender">
            <LenderShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<Overview />} />
        <Route path="upload/*" element={<UploadLayout />} />
        <Route path="listed" element={<ListedProducts />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="profile" element={<Profile />} />
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="overview" replace />} />
      </Route>

      <Route path="/admin/signin" element={<AdminSignIn />} />

      <Route
        path="/admin"
        element={
          <RequireAdminAuth>
            <AdminLayout />
          </RequireAdminAuth>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="products" element={<ProductManagementPage />} />
        <Route path="borrowers" element={<BorrowerManagementPage />} />
        <Route path="lenders" element={<LenderManagementPage />} />
        <Route path="analytics" element={<AnalyticsReportsPage />} />
        <Route path="reviews" element={<ReviewsEngagementPage />} />
        <Route path="callbacks" element={<RequestedCallbacksPage />} />
        <Route path="users" element={<UserRoleManagementPage />} />
        <Route path="calendar" element={<ApplicationCalendarPage />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<RedirectToSiteHome />} />
    </Routes>
    </>
  );
}
